/**
 * CedoliniStorico — Storico modificabile delle paghe mensili di un lavoratore.
 *
 * Ogni mese ha 3 campi editabili:
 *   - Paga base
 *   - N. ore straordinarie
 *   - Costo ora straordinaria
 * Il totale (Importo cedolino) è calcolato automaticamente e salvato dal backend.
 *
 * Ciascun mese è INDIPENDENTE: modificando settembre non si toccano i mesi
 * precedenti, che restano consultabili nella tabella.
 *
 * Props:
 *   lavoratoreId  — string (obbligatoria; senza questa il componente non renderizza nulla)
 */
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Save, History } from "lucide-react";
import api from "@/api";

const MESI = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];
const eur = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function CedoliniStorico({ lavoratoreId }) {
  const currentYear = new Date().getFullYear();
  const [anno, setAnno] = useState(currentYear);
  const [drafts, setDrafts] = useState({}); // { mese: {paga_base, ore, costo, dirty} }
  const qc = useQueryClient();

  const { data: cedolini = [], isLoading } = useQuery({
    queryKey: ["cedolini", lavoratoreId, anno],
    queryFn: () => api.get(`/cedolini/${lavoratoreId}/${anno}`).then((r) => r.data),
    enabled: !!lavoratoreId,
  });

  const salva = useMutation({
    mutationFn: ({ mese, payload }) => api.put(`/cedolini/${lavoratoreId}/${anno}/${mese}`, payload).then((r) => r.data),
    onSuccess: (_, vars) => {
      toast.success(`Cedolino ${MESI[vars.mese - 1]} ${anno} salvato`);
      setDrafts((d) => { const n = {...d}; delete n[vars.mese]; return n; });
      qc.invalidateQueries({ queryKey: ["cedolini", lavoratoreId, anno] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  if (!lavoratoreId) return null;

  const getValue = (row, key) => {
    const d = drafts[row.mese];
    if (d && d[key] !== undefined) return d[key];
    return row[key];
  };

  const setField = (mese, key, value) => {
    setDrafts((d) => ({
      ...d,
      [mese]: { ...(d[mese] || {}), [key]: value, dirty: true },
    }));
  };

  const totaleMese = (row) => {
    const paga = Number(getValue(row, "paga_base")) || 0;
    const ore = Number(getValue(row, "ore_straordinarie")) || 0;
    const costo = Number(getValue(row, "costo_ora_straordinaria")) || 0;
    return paga + ore * costo;
  };

  const salvaMese = (row) => {
    const payload = {
      paga_base: Number(getValue(row, "paga_base")) || 0,
      ore_straordinarie: Number(getValue(row, "ore_straordinarie")) || 0,
      costo_ora_straordinaria: Number(getValue(row, "costo_ora_straordinaria")) || 0,
      note: getValue(row, "note") || "",
    };
    salva.mutate({ mese: row.mese, payload });
  };

  const totaleAnno = cedolini.reduce((s, r) => s + totaleMese(r), 0);

  return (
    <section data-testid="section-storico-cedolini">
      <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2">
        <History className="w-4 h-4" /> Storico Cedolini Mensili
      </div>
      <div className="flex items-center gap-3 mb-3">
        <label className="text-xs font-semibold text-slate-600">Anno:</label>
        <select
          value={anno}
          onChange={(e) => setAnno(Number(e.target.value))}
          data-testid="select-anno-cedolini"
          className="px-2 py-1 border border-slate-300 rounded-sm text-sm"
        >
          {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <div className="ml-auto text-xs text-slate-600">
          Totale anno: <b className="font-mono text-slate-900">{eur(totaleAnno)}</b>
        </div>
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left p-2">Mese</th>
              <th className="text-right p-2">Paga base €</th>
              <th className="text-right p-2">N. ore straord.</th>
              <th className="text-right p-2">€/h straord.</th>
              <th className="text-right p-2 bg-emerald-50">Importo cedolino</th>
              <th className="text-center p-2">Stato</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={7} className="p-4 text-center text-slate-400">Caricamento…</td></tr>
            )}
            {!isLoading && cedolini.map((row) => {
              const isDirty = !!drafts[row.mese]?.dirty;
              const totale = totaleMese(row);
              return (
                <tr
                  key={row.mese}
                  data-testid={`cedolino-row-${row.mese}`}
                  className={`border-t border-slate-100 ${isDirty ? "bg-amber-50" : row.exists ? "" : "opacity-70"}`}
                >
                  <td className="p-2 font-semibold">{MESI[row.mese - 1]}</td>
                  <td className="p-1">
                    <input
                      type="number" step="0.01" min="0"
                      value={getValue(row, "paga_base") ?? 0}
                      onChange={(e) => setField(row.mese, "paga_base", e.target.value)}
                      data-testid={`ced-paga-${row.mese}`}
                      className="w-full text-right px-2 py-1 border border-slate-200 rounded-sm font-mono"
                    />
                  </td>
                  <td className="p-1">
                    <input
                      type="number" step="0.5" min="0"
                      value={getValue(row, "ore_straordinarie") ?? 0}
                      onChange={(e) => setField(row.mese, "ore_straordinarie", e.target.value)}
                      data-testid={`ced-ore-${row.mese}`}
                      className="w-full text-right px-2 py-1 border border-slate-200 rounded-sm font-mono"
                    />
                  </td>
                  <td className="p-1">
                    <input
                      type="number" step="0.1" min="0"
                      value={getValue(row, "costo_ora_straordinaria") ?? 0}
                      onChange={(e) => setField(row.mese, "costo_ora_straordinaria", e.target.value)}
                      data-testid={`ced-costo-${row.mese}`}
                      className="w-full text-right px-2 py-1 border border-slate-200 rounded-sm font-mono"
                    />
                  </td>
                  <td className="p-2 text-right font-mono font-bold bg-emerald-50" data-testid={`ced-totale-${row.mese}`}>
                    {eur(totale)}
                  </td>
                  <td className="text-center p-2">
                    {row.exists ? (
                      <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 rounded-sm">
                        Salvato
                      </span>
                    ) : (
                      <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-500 rounded-sm">
                        Default
                      </span>
                    )}
                  </td>
                  <td className="p-1 text-right">
                    <button
                      onClick={() => salvaMese(row)}
                      disabled={!isDirty || salva.isPending}
                      data-testid={`ced-save-${row.mese}`}
                      title="Salva SOLO questo mese"
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold bg-orange-600 hover:bg-orange-700 disabled:opacity-40 text-white rounded-sm"
                    >
                      <Save className="w-3 h-3" /> Salva
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-500 mt-2">
        <b>Storico protetto</b>: modificando i valori di un mese e cliccando <b>Salva</b>, gli altri
        mesi restano invariati. I mesi non ancora compilati mostrano i valori di default (Paga base +
        Costo ora straord. dall'anagrafica) e appaiono in grigio finché non vengono salvati.
      </p>
    </section>
  );
}
